import { cn } from '@kalos/ui';
import { type ReactNode, useState } from 'react';
import { type FieldValues, type UseFormReturn } from 'react-hook-form';
import { type ZodType } from 'zod';

import { MobileSearchBar } from '../modules/ComponentsLibrary/AdvancedSearch/MobileSearchParts';
import { EmptyState } from './EmptyState';
import {
  ActiveFilterChips,
  type FilterDimension,
  FilterSheet,
  type FilterSheetPanel,
  getActiveFilterDimensions,
} from './FilterSheet';
import { ListRows } from './ListRow';
import { LoadErrorState } from './LoadErrorState';
import { LoadMoreSentinel } from './LoadMoreSentinel';

export type ListFiltersPhone<TFilters extends FieldValues> = {
  dimensions: ReadonlyArray<FilterDimension<TFilters>>;
  /** The filters applied to the list. */
  values: TFilters;
  defaultValues: TFilters;
  onApply: (values: TFilters) => void;
  /** The sheet's title: "Filter reimbursables". */
  sheetTitle: string;
  sheetDescription?: string;
  /** "Sort" when the sheet only orders the list. */
  buttonLabel?: string;
  /** Rules across fields, such as a date range that needs both ends. Checked on Apply. */
  schema?: ZodType<TFilters>;
  /** The sheet's fields, bound to its staged form. */
  renderFields?: (form: UseFormReturn<TFilters>) => ReactNode;
  /** Drill-in rows instead of fields, for long option lists. */
  panels?: ReadonlyArray<FilterSheetPanel<TFilters>>;
  /** A sheet of the list's own, when `FilterSheet`'s fields and panels don't fit it. */
  renderSheet?: (sheet: { open: boolean; onOpenChange: (open: boolean) => void }) => ReactNode;
};

/**
 * The top of a phone list: one search box with Filter inside it, the applied filters as removable
 * chips, and the filter sheet, whose open state lives here. Every list on a phone or tablet reads
 * alike because they all start with this.
 */
export const ListSearchPhone = <TFilters extends FieldValues>({
  search,
  filters,
}: {
  search?: { value: string; onChange: (value: string) => void; placeholder: string };
  filters?: ListFiltersPhone<TFilters>;
}) => {
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const activeFilterCount = filters
    ? getActiveFilterDimensions(filters.dimensions, filters.values, filters.defaultValues).length
    : 0;

  return (
    <>
      {search && (
        <MobileSearchBar
          value={search.value}
          onChange={search.onChange}
          placeholder={search.placeholder}
          activeFilterCount={activeFilterCount}
          onOpenFilters={filters ? () => setIsFilterSheetOpen(true) : undefined}
          filterLabel={filters?.buttonLabel}
        />
      )}
      {filters && (
        <>
          <ActiveFilterChips
            dimensions={filters.dimensions}
            values={filters.values}
            defaultValues={filters.defaultValues}
            onChange={filters.onApply}
          />
          {filters.renderSheet ? (
            filters.renderSheet({ open: isFilterSheetOpen, onOpenChange: setIsFilterSheetOpen })
          ) : (
            <FilterSheet
              open={isFilterSheetOpen}
              onOpenChange={setIsFilterSheetOpen}
              title={filters.sheetTitle}
              description={filters.sheetDescription}
              values={filters.values}
              defaultValues={filters.defaultValues}
              schema={filters.schema}
              onApply={filters.onApply}
              panels={filters.panels}
            >
              {filters.renderFields}
            </FilterSheet>
          )}
        </>
      )}
    </>
  );
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

export type ListResultsPhoneProps = {
  /** Plural noun for the records, for example "documents". Words the states and the count. */
  itemLabel: string;
  /** Rows on screen. Lists that render their pages themselves pass the total instead. */
  rowCount: number;
  /** For the count line and the end-of-list line. Defaults to `rowCount`. */
  totalCount?: number;
  /** Rows loaded so far, for the end-of-list line, when it isn't `rowCount`. */
  loadedCount?: number;
  isPending: boolean;
  isFetching: boolean;
  /** The first load failed. Shown as an error only while there are no rows. */
  isError: boolean;
  /** The next page failed, rather than the first. Defaults to `isError`. */
  isNextPageError?: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  onRetry?: () => void;
  /** Retry for the next page. Defaults to `onLoadMore`, then `onRetry`. */
  onRetryNextPage?: () => void;
  /** A search or filter is applied, so an empty list is "no match", not "nothing yet". */
  isFiltered?: boolean;
  onClearFilters?: () => void;
  /** The list's own wording for nothing to show ("No results.", "No jobs found"). */
  emptyTitle: string;
  emptyDescription?: string;
  /** The control that changes nothing-yet, if there is one. */
  emptyAction?: ReactNode;
  filteredEmptyTitle?: string;
  filteredEmptyDescription?: string;
  /** Rows are filtered on the client: nothing matched so far, but more pages may. */
  loadingMoreNote?: string;
  errorTitle?: string;
  errorDescription?: string;
  retryLabel?: string;
  loadingLabel?: string;
  loadingRows?: number;
  /** A line under the count about how complete the list is, such as a search's limit. */
  note?: string;
  /** One control beside the count, such as a "More actions" menu. */
  headerAction?: ReactNode;
  /** The "N items" line. Off where a heading above already gives the count. */
  showCount?: boolean;
  /**
   * `card` boxes the rows (the default). `flush` leaves them edge to edge. `inCard` sits inside a
   * card that draws its own border: the rows under a rule, and nothing-to-show as one quiet line.
   */
  variant?: 'card' | 'flush' | 'inCard';
  /** Names the rows for screen readers. Defaults to `itemLabel`, capitalised. */
  rowsLabel?: string;
  rowsClassName?: string;
  /** The children are not list items (cards, a form): render them as given, not in a list. */
  rowsAsGiven?: boolean;
  /** Under the rows, inside the box: a caption, or an "All …" row. */
  footer?: ReactNode;
  children: ReactNode;
};

const LoadingRows = ({ count, className }: { count: number; className?: string }) => (
  <div className={cn('divide-border divide-y', className)} aria-hidden>
    {Array.from({ length: count }, (_, index) => (
      <div key={index} className="flex flex-col gap-2 px-4 py-4">
        <div className="bg-muted h-4 w-2/3 animate-pulse rounded" />
        <div className="bg-muted h-3 w-1/2 animate-pulse rounded" />
      </div>
    ))}
  </div>
);

/**
 * The body of a phone list: the count, then the rows, loading more as the list scrolls. Loading,
 * a failed load (with Retry), nothing yet and nothing matching are all worded, and every list on
 * a phone or tablet shows them the same way.
 */
export const ListResultsPhone = ({
  itemLabel,
  rowCount,
  totalCount = rowCount,
  loadedCount = rowCount,
  isPending,
  isFetching,
  isError,
  isNextPageError = isError,
  isFetchingNextPage = false,
  hasNextPage = false,
  onLoadMore,
  onRetry,
  onRetryNextPage,
  isFiltered = false,
  onClearFilters,
  emptyTitle,
  emptyDescription,
  emptyAction,
  filteredEmptyTitle = emptyTitle,
  filteredEmptyDescription = 'Try a different search, or clear the filters.',
  loadingMoreNote,
  errorTitle = `Couldn't load ${itemLabel}`,
  errorDescription,
  retryLabel = `Retry loading ${itemLabel}`,
  loadingLabel = `Loading ${itemLabel}…`,
  loadingRows = 6,
  note,
  headerAction,
  showCount = true,
  variant = 'card',
  rowsLabel = capitalize(itemLabel),
  rowsClassName,
  rowsAsGiven = false,
  footer,
  children,
}: ListResultsPhoneProps) => {
  const inCard = variant === 'inCard';
  const boxClass = variant === 'card' ? 'bg-card border-border rounded-lg border' : '';

  if (isError && rowCount === 0) {
    return (
      <LoadErrorState
        className={inCard ? 'mx-4 mb-4 w-auto' : undefined}
        title={errorTitle}
        description={errorDescription}
        retryLabel={retryLabel}
        onRetry={onRetry ?? (() => undefined)}
        isRetrying={isFetching}
      />
    );
  }

  const countNoun = totalCount === 1 ? itemLabel.replace(/s$/, '') : itemLabel;
  const header = (showCount || headerAction) && (
    <div className="flex min-h-9 items-center justify-between gap-2">
      {showCount && (
        <p className="text-muted-foreground text-sm tabular-nums" role="status">
          {isPending ? loadingLabel : `${totalCount.toLocaleString()} ${countNoun}`}
        </p>
      )}
      {headerAction}
    </div>
  );

  let body: ReactNode;
  if (isPending) {
    body = (
      <div role="status" aria-label={loadingLabel}>
        <span className="sr-only">{loadingLabel}</span>
        <LoadingRows count={loadingRows} className={cn(boxClass, inCard && 'border-t')} />
      </div>
    );
  } else if (rowCount === 0 && hasNextPage && loadingMoreNote) {
    body = (
      <>
        <p className="text-muted-foreground text-sm">{loadingMoreNote}</p>
        <LoadMoreSentinel
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isError={isNextPageError}
          onLoadMore={onLoadMore ?? (() => undefined)}
          onRetry={onRetryNextPage ?? onLoadMore ?? onRetry}
          loadedCount={loadedCount}
          itemLabel={itemLabel}
        />
      </>
    );
  } else if (rowCount === 0 && inCard) {
    body = (
      <div role="status" className="text-muted-foreground flex flex-col gap-1 px-4 pb-4 text-sm">
        <p>{isFiltered ? filteredEmptyTitle : emptyTitle}</p>
        {!isFiltered && emptyDescription && <p>{emptyDescription}</p>}
      </div>
    );
  } else if (rowCount === 0) {
    body =
      isFiltered && onClearFilters ? (
        <EmptyState
          variant="filtered"
          title={filteredEmptyTitle}
          description={filteredEmptyDescription}
          onClearFilters={onClearFilters}
          className={variant === 'card' ? boxClass : undefined}
        />
      ) : (
        <EmptyState
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
          className={variant === 'card' ? boxClass : undefined}
        />
      );
  } else {
    body = (
      <div className={cn(variant !== 'flush' && 'overflow-hidden', boxClass, inCard && 'border-t')}>
        {rowsAsGiven ? (
          children
        ) : (
          <ListRows label={rowsLabel} className={rowsClassName}>
            {children}
          </ListRows>
        )}
        <LoadMoreSentinel
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isError={isNextPageError}
          onLoadMore={onLoadMore ?? (() => undefined)}
          onRetry={onRetryNextPage ?? onLoadMore ?? onRetry}
          loadedCount={loadedCount}
          itemLabel={itemLabel}
        />
        {footer}
      </div>
    );
  }

  if (!header && !note) return body;
  return (
    <div className="flex flex-col gap-2">
      {header}
      {note && <p className="text-muted-foreground text-sm">{note}</p>}
      {body}
    </div>
  );
};
